import React, { useId, useMemo, useState } from 'react';
import { Box, Button, ButtonBase, IconButton, LinearProgress, Popover, Skeleton, Tooltip, Typography, useMediaQuery } from '@mui/material';
import type { Theme } from '@mui/material';
import { ChevronLeft, ChevronRight, TriangleAlert, X } from 'lucide-react';
import {
  addMonths,
  addWeeks,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  isToday,
  parseISO,
  startOfMonth,
  startOfWeek,
  subMonths,
  subWeeks,
} from 'date-fns';
import { Link } from 'react-router-dom';
import {
  useGetFilteredTimeOffEntriesQuery,
  TimeOffEntry,
  TimeOffStatus,
  TimeOffType,
} from '../../store/slices/timeOffSlice';
import { mix, sbg } from '../../theme/sbgTokens';
import { ErrorState, PersonAvatar, SegmentedControl, StatusPill, useDelayedFlag } from '../ui';
import { formatCount, formatDate, formatDateRange, formatNumber, formatTime, MISSING } from '../../utils/format';
import {
  outsideTint,
  pendingBorder,
  pendingHatch,
  SeriesChipStyle,
  TIME_OFF_TYPE_ORDER,
  timeOffTypeChipStyle,
  timeOffTypeLabel,
} from './categoryChipStyle';
import { SeriesDot, TimeOffTypeChip } from './calendar-parts/SeriesChip';
import { describeRequestFailure, failureAdvice, formatHours } from './calendar-parts/calendarFormat';
import ErrorDetails from './calendar-parts/ErrorDetails';

type CalendarViewMode = 'month' | 'week';

const WEEKDAYS = [
  { short: 'Sun', narrow: 'S', long: 'Sunday' },
  { short: 'Mon', narrow: 'M', long: 'Monday' },
  { short: 'Tue', narrow: 'T', long: 'Tuesday' },
  { short: 'Wed', narrow: 'W', long: 'Wednesday' },
  { short: 'Thu', narrow: 'T', long: 'Thursday' },
  { short: 'Fri', narrow: 'F', long: 'Friday' },
  { short: 'Sat', narrow: 'S', long: 'Saturday' },
];

/** Event lanes shown in a week row before "+N more" (month view gets a 4th lane at lg). */
const LANES = { month: 3, monthLg: 4, week: 12 } as const;
/** Bar height + the 2px gap between lanes. */
const LANE_PITCH = 22;
/** Where lanes start inside a week row at sm+: 6px cell padding + the 24px date row + 4px. */
const LANE_TOP = 34;
/** Bar inset from a cell edge when the entry starts or ends inside the row. */
const BAR_INSET = '6px';

/**
 * Placeholder bars while the first load is in flight, as [first column, span,
 * lane]. Two or three per week row, mostly on weekdays, so the grid reads as
 * "loading" — never as a month where nobody is out.
 */
const SKELETON_WEEKS: ReadonlyArray<ReadonlyArray<readonly [number, number, number]>> = [
  [[1, 3, 0], [5, 1, 0], [2, 2, 1]],
  [[1, 2, 0], [3, 3, 0], [4, 1, 1]],
  [[2, 4, 0], [1, 2, 1]],
  [[1, 1, 0], [3, 2, 0], [1, 4, 1]],
  [[4, 2, 0], [1, 2, 0], [2, 1, 1]],
  [[2, 2, 0], [5, 1, 0], [3, 3, 1]],
];

interface SkeletonBar {
  startCol: number;
  span: number;
  lane: number;
}

const insetFocus = { outline: `2px solid ${sbg.focus.ring}`, outlineOffset: '-2px' } as const;

interface CalendarEntry {
  _id: string;
  employeeName: string;
  employeeId: string;
  department: string;
  type: string;
  status: string;
  hours: number;
  startDate: string;
  endDate: string;
  /** yyyy-MM-dd of the first and last day, for lane and continuation math. */
  startKey: string;
  endKey: string;
  notes?: string;
}

/** One bar in a week row: an entry's run of days, split at the month edge. */
interface Segment {
  entry: CalendarEntry;
  lane: number;
  startCol: number;
  span: number;
  outside: boolean;
  /** The entry continues past this segment's left / right edge. */
  joinStart: boolean;
  joinEnd: boolean;
}

interface WeekRow {
  days: Date[];
  keys: string[];
  /** Lane per entry id; every day of the week renders the entry in the same lane. */
  lanes: Map<string, number>;
  laneCount: number;
  segments: Segment[];
}

interface TimeOffCalendarViewProps {
  entries?: TimeOffEntry[];
}

const dayKey = (day: Date) => format(day, 'yyyy-MM-dd');
const isPending = (entry: CalendarEntry) => entry.status === TimeOffStatus.PENDING;
const isRemote = (entry: CalendarEntry) => entry.type === TimeOffType.WFH;

const typeRank = (type: string) => {
  const index = (TIME_OFF_TYPE_ORDER as string[]).indexOf(type);
  return index === -1 ? TIME_OFF_TYPE_ORDER.length : index;
};

/** Day lists and the popover: pending first (HR acts on those), then type order, then name. */
const byDayOrder = (a: CalendarEntry, b: CalendarEntry) =>
  Number(isPending(b)) - Number(isPending(a)) ||
  typeRank(a.type) - typeRank(b.type) ||
  a.employeeName.localeCompare(b.employeeName);

/**
 * Lane assignment for one week row: pending first so it is never the entry
 * hidden under "+N more", then by start day and longest run, then type and
 * name. Each entry takes the first lane free on all of its days, so a
 * multi-day entry keeps one row and draws as one bar.
 */
const layoutWeek = (
  keys: string[],
  dayMap: Map<string, CalendarEntry[]>,
  isOutside: (col: number) => boolean,
): Omit<WeekRow, 'days' | 'keys'> => {
  const runs = new Map<string, { entry: CalendarEntry; first: number; last: number }>();
  keys.forEach((key, col) => {
    (dayMap.get(key) ?? []).forEach((entry) => {
      const run = runs.get(entry._id);
      if (run) run.last = col;
      else runs.set(entry._id, { entry, first: col, last: col });
    });
  });

  const ordered = Array.from(runs.values()).sort(
    (a, b) =>
      Number(isPending(b.entry)) - Number(isPending(a.entry)) ||
      a.first - b.first ||
      b.last - b.first - (a.last - a.first) ||
      typeRank(a.entry.type) - typeRank(b.entry.type) ||
      a.entry.employeeName.localeCompare(b.entry.employeeName),
  );

  const occupied: boolean[][] = [];
  const lanes = new Map<string, number>();
  const segments: Segment[] = [];

  ordered.forEach(({ entry, first, last }) => {
    let lane = 0;
    while (occupied[lane] && occupied[lane].slice(first, last + 1).some(Boolean)) lane += 1;
    if (!occupied[lane]) occupied[lane] = new Array(keys.length).fill(false);
    for (let col = first; col <= last; col += 1) occupied[lane][col] = true;
    lanes.set(entry._id, lane);

    // Split the run where it crosses the month edge so outside days stay quiet.
    let start = first;
    for (let col = first; col <= last; col += 1) {
      if (col === last || isOutside(col + 1) !== isOutside(col)) {
        segments.push({
          entry,
          lane,
          startCol: start,
          span: col - start + 1,
          outside: isOutside(start),
          joinStart: entry.startKey < keys[start],
          joinEnd: entry.endKey > keys[col],
        });
        start = col + 1;
      }
    }
  });

  return { lanes, laneCount: occupied.length, segments };
};

/** People off (PTO, Sick) and working remotely (WFH), counted once each. */
const splitPeople = (entries: CalendarEntry[]) => {
  const off = new Set<string>();
  const remote = new Set<string>();
  entries.forEach((entry) => (isRemote(entry) ? remote : off).add(entry.employeeId));
  return { off: off.size, remote: remote.size };
};

const describePeople = ({ off, remote }: { off: number; remote: number }) => {
  const parts: string[] = [];
  if (off > 0) parts.push(`${formatCount(off, 'person', 'people')} off`);
  if (remote > 0) parts.push(off > 0 ? `${formatNumber(remote)} remote` : `${formatCount(remote, 'person', 'people')} remote`);
  return parts;
};

const describeDay = (entries: CalendarEntry[]) => {
  const pending = entries.filter(isPending).length;
  const parts = describePeople(splitPeople(entries));
  if (pending > 0) parts.push(`${formatNumber(pending)} pending approval`);
  return parts.join(' · ');
};

const peopleIn = (entries: CalendarEntry[]) => new Set(entries.map((entry) => entry.employeeId)).size;

/** "18 PTO · 3 Sick · 2 WFH" counts, in palette order. */
const countByType = (entries: CalendarEntry[]) => {
  const counts = new Map<string, number>();
  entries.forEach((entry) => counts.set(entry.type, (counts.get(entry.type) ?? 0) + 1));
  const known = TIME_OFF_TYPE_ORDER.filter((type) => counts.has(type)) as string[];
  const other = Array.from(counts.keys()).filter((type) => !known.includes(type));
  return [...known, ...other].map((type) => ({ type, count: counts.get(type) ?? 0 }));
};

/* ------------------------------------------------------------------------ */
/* Pieces                                                                    */
/* ------------------------------------------------------------------------ */

/** The hatched end cap on a pending bar: full-strength stripes on the surface, never behind text. */
const PendingCap: React.FC<{ tone: SeriesChipStyle }> = ({ tone }) => (
  <Box
    component="span"
    aria-hidden="true"
    sx={{ flex: 'none', alignSelf: 'stretch', width: 8, background: pendingHatch(tone.dot), borderLeft: pendingBorder(tone) }}
  />
);

/**
 * A compact 20px event bar: series dot, name, tinted fill. Pending bars are
 * on the surface with a dashed full-color outline, a hollow dot, a hatched
 * cap and — when the bar is wide enough — a "Pending" label.
 */
const EventBar: React.FC<{
  entry: CalendarEntry;
  outside?: boolean;
  showType?: boolean;
  joinStart?: boolean;
  joinEnd?: boolean;
  placement?: { gridColumn: string; gridRow: number; ml: string | number; mr: string | number };
}> = ({ entry, outside = false, showType = false, joinStart = false, joinEnd = false, placement }) => {
  const tone = timeOffTypeChipStyle(entry.type);
  const pending = isPending(entry);
  const edge = pending ? pendingBorder(tone) : `1px solid ${tone.border}`;
  return (
    <Box
      component="span"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        height: 20,
        minWidth: 0,
        pl: 0.75,
        pr: pending ? 0 : 0.75,
        overflow: 'hidden',
        containerType: 'inline-size',
        border: edge,
        ...(joinStart ? { borderLeft: 0 } : null),
        ...(joinEnd ? { borderRight: 0 } : null),
        borderRadius: sbg.radius.sm,
        ...(joinStart ? { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 } : null),
        ...(joinEnd ? { borderTopRightRadius: 0, borderBottomRightRadius: 0 } : null),
        bgcolor: pending ? sbg.bg.surface : outside ? outsideTint(tone) : tone.bg,
        color: outside ? sbg.text.secondary : sbg.text.primary,
        font: 'var(--sbg-type-label-xs)',
        ...placement,
      }}
    >
      <SeriesDot color={tone.dot} hollow={pending} />
      <Box component="span" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {entry.employeeName}
      </Box>
      {showType && !pending && (
        <Box component="span" sx={{ flex: 'none', color: sbg.text.secondary, display: 'none', '@container (min-width: 168px)': { display: 'inline' } }}>
          {tone.label}
        </Box>
      )}
      {pending && (
        <>
          <Box
            component="span"
            sx={{ flex: 'none', color: sbg.status.warning.text, display: 'none', '@container (min-width: 136px)': { display: 'inline' } }}
          >
            Pending
          </Box>
          <PendingCap tone={tone} />
        </>
      )}
    </Box>
  );
};

/** The date row: numeral (Blue 500 disc for today, "Nov 1" on month starts) and the day's head count. */
const DayHeader: React.FC<{
  day: Date;
  today: boolean;
  outside: boolean;
  weekdayOnPhone: boolean;
  people: number;
  countDisplay: Record<string, string>;
}> = ({ day, today, outside, weekdayOnPhone, people, countDisplay }) => {
  const label = day.getDate() === 1 && !today ? format(day, 'MMM d') : format(day, 'd');
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, height: 24, mb: 0.5, minWidth: 0 }}>
      {weekdayOnPhone && (
        <Typography variant="labelXs" component="span" sx={{ color: sbg.text.secondary, display: { xs: 'inline', sm: 'none' }, minWidth: 28 }}>
          {format(day, 'EEE')}
        </Typography>
      )}
      <Box
        component="span"
        sx={{
          display: 'inline-grid',
          placeItems: 'center',
          minWidth: 24,
          height: 24,
          px: label.length > 2 ? 0.75 : 0,
          borderRadius: sbg.radius.pill,
          bgcolor: today ? sbg.action.primary : 'transparent',
          color: today ? sbg.text.onBrand : outside ? sbg.text.secondary : sbg.text.primary,
          font: 'var(--sbg-type-label-sm)',
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
          flex: 'none',
        }}
      >
        {label}
      </Box>
      {today && (
        <Box component="span" className="sbg-sr-only">
          Today
        </Box>
      )}
      {people > 0 && (
        <Typography
          variant="labelXs"
          component="span"
          aria-hidden="true"
          sx={{ ml: 'auto', color: sbg.text.secondary, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', display: countDisplay }}
        >
          {formatNumber(people)} away
        </Typography>
      )}
    </Box>
  );
};

const MoreLine: React.FC<{ count: number; smUp?: boolean }> = ({ count, smUp = false }) => (
  <Typography
    variant="labelXs"
    component="span"
    sx={{
      display: smUp ? { xs: 'none', sm: 'block' } : 'block',
      color: sbg.text.secondary,
      px: 0.75,
      pt: 0.25,
      fontVariantNumeric: 'tabular-nums',
    }}
  >
    +{formatNumber(count)} more
  </Typography>
);

/* ------------------------------------------------------------------------ */
/* Calendar                                                                  */
/* ------------------------------------------------------------------------ */

const TimeOffCalendarView: React.FC<TimeOffCalendarViewProps> = ({ entries: providedEntries }) => {
  const [view, setView] = useState<CalendarViewMode>('month');
  const [anchor, setAnchor] = useState(() => new Date());
  const [selectedDayKey, setSelectedDayKey] = useState<string | null>(null);
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const popoverTitleId = useId();
  const groupIdPrefix = useId();
  const wideMonth = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));

  const {
    data: fetchedTimeOffEntries = [],
    isLoading,
    isFetching,
    isError,
    error,
    fulfilledTimeStamp,
    refetch,
  } = useGetFilteredTimeOffEntriesQuery({}, { skip: Boolean(providedEntries) });
  const timeOffEntries = providedEntries ?? fetchedTimeOffEntries;
  const ownsData = !providedEntries;
  // Nothing to show yet: the first load, or a retry after it failed (RTK Query
  // only flags the very first request as isLoading). Busy: any request in flight.
  const loading = ownsData && Boolean(isLoading || (isFetching && !fulfilledTimeStamp));
  const busy = ownsData && Boolean(isLoading || isFetching);
  const showSkeleton = useDelayedFlag(loading, 300);
  const showProgress = useDelayedFlag(busy, 300);
  const loadFailed = ownsData && isError && fetchedTimeOffEntries.length === 0;
  // A background refetch failed but cached data is still on screen: say it's stale.
  const refreshFailed = ownsData && isError && fetchedTimeOffEntries.length > 0;
  const failure = isError ? describeRequestFailure(error) : null;

  const laneLimit = view === 'week' ? LANES.week : wideMonth ? LANES.monthLg : LANES.month;

  // Entries per day, plus a lane layout per week row
  const calendarData = useMemo(() => {
    const viewStart = view === 'month' ? startOfWeek(startOfMonth(anchor)) : startOfWeek(anchor);
    const viewEnd = view === 'month' ? endOfWeek(endOfMonth(anchor)) : endOfWeek(anchor);

    const dayMap = new Map<string, CalendarEntry[]>();
    const allDays = eachDayOfInterval({ start: viewStart, end: viewEnd });
    allDays.forEach((day) => dayMap.set(dayKey(day), []));

    timeOffEntries
      .filter((e) => e.status !== TimeOffStatus.REJECTED)
      .forEach((e) => {
        try {
          const start = parseISO(e.startDate);
          const end = parseISO(e.endDate);
          const entry: CalendarEntry = {
            _id: e._id,
            employeeName: e.employee.name,
            employeeId: e.employee._id,
            department: e.employee.department?.name || '',
            type: e.type,
            status: e.status,
            hours: e.hours,
            startDate: e.startDate,
            endDate: e.endDate,
            startKey: dayKey(start),
            endKey: dayKey(end),
            notes: e.notes,
          };
          eachDayOfInterval({ start, end }).forEach((day) => {
            dayMap.get(dayKey(day))?.push(entry);
          });
        } catch {
          /* skip invalid dates */
        }
      });
    dayMap.forEach((list) => list.sort(byDayOrder));

    const weeks: WeekRow[] = [];
    for (let i = 0; i < allDays.length; i += 7) {
      const days = allDays.slice(i, i + 7);
      const keys = days.map(dayKey);
      const isOutside = (col: number) => view === 'month' && !isSameMonth(days[col], anchor);
      weeks.push({ days, keys, ...layoutWeek(keys, dayMap, isOutside) });
    }

    return { dayMap, viewStart, viewEnd, weeks };
  }, [anchor, view, timeOffEntries]);

  const inPeriod = (day: Date) => view === 'week' || isSameMonth(day, anchor);

  /** A week row's placeholder bars, clipped to the days inside the period. */
  const skeletonBarsFor = (week: WeekRow, weekIdx: number): SkeletonBar[] => {
    const first = week.days.findIndex(inPeriod);
    const last = week.days.length - 1 - [...week.days].reverse().findIndex(inPeriod);
    if (first === -1) return [];
    return SKELETON_WEEKS[weekIdx % SKELETON_WEEKS.length].flatMap(([col, span, lane]) => {
      const startCol = Math.max(col, first);
      const endCol = Math.min(col + span - 1, last);
      return startCol <= endCol ? [{ startCol, span: endCol - startCol + 1, lane }] : [];
    });
  };

  // Unique requests and people inside the period (the month, or the week)
  const periodStats = useMemo(() => {
    const byType = new Map<string, number>();
    const seen = new Set<string>();
    const periodEntries: CalendarEntry[] = [];
    let pending = 0;
    calendarData.dayMap.forEach((entries, key) => {
      if (view === 'month' && !isSameMonth(parseISO(key), anchor)) return;
      entries.forEach((entry) => {
        periodEntries.push(entry);
        if (seen.has(entry._id)) return;
        seen.add(entry._id);
        byType.set(entry.type, (byType.get(entry.type) ?? 0) + 1);
        if (isPending(entry)) pending += 1;
      });
    });
    return { byType, requests: seen.size, people: splitPeople(periodEntries), pending };
  }, [calendarData, view, anchor]);

  const periodLabel =
    view === 'month'
      ? format(anchor, 'MMMM yyyy')
      : formatDateRange(dayKey(calendarData.viewStart), dayKey(calendarData.viewEnd));
  const periodNoun = view === 'month' ? 'month' : 'week';

  const summary = (() => {
    if (loading) return showSkeleton ? 'Loading time off…' : ' ';
    if (loadFailed) return 'Time off couldn’t be loaded';
    if (periodStats.requests === 0) return `No time off scheduled this ${periodNoun}`;
    const parts = [...describePeople(periodStats.people), formatCount(periodStats.requests, 'request')];
    if (periodStats.pending > 0) parts.push(`${formatNumber(periodStats.pending)} pending approval`);
    return parts.join(' · ');
  })();

  const handlePrev = () => setAnchor((current) => (view === 'month' ? subMonths(current, 1) : subWeeks(current, 1)));
  const handleNext = () => setAnchor((current) => (view === 'month' ? addMonths(current, 1) : addWeeks(current, 1)));
  const handleToday = () => setAnchor(new Date());
  const handleViewChange = (next: CalendarViewMode) => {
    if (next === view) return;
    if (next === 'week') {
      // Land on this week when today is in the month on screen, else the month's first week.
      setAnchor((current) => (isSameMonth(new Date(), current) ? new Date() : startOfMonth(current)));
    }
    setView(next);
  };

  const openDay = (target: HTMLElement, key: string) => {
    setPopoverAnchor(target);
    setSelectedDayKey(key);
  };
  const closeDay = () => {
    setPopoverAnchor(null);
    setSelectedDayKey(null);
  };

  const selectedDayEntries = useMemo(
    () => (selectedDayKey ? calendarData.dayMap.get(selectedDayKey) ?? [] : []),
    [calendarData, selectedDayKey],
  );

  // Popover groups: pending approval first, then approved by type.
  const dayGroups = useMemo(() => {
    const groups: Array<{ key: string; label: string; pending: boolean; tone: SeriesChipStyle | null; entries: CalendarEntry[] }> = [];
    const pending = selectedDayEntries.filter(isPending);
    if (pending.length > 0) groups.push({ key: 'pending', label: 'Pending approval', pending: true, tone: null, entries: pending });
    const approved = selectedDayEntries.filter((entry) => !isPending(entry));
    countByType(approved).forEach(({ type }) => {
      groups.push({
        key: type,
        label: timeOffTypeLabel(type),
        pending: false,
        tone: timeOffTypeChipStyle(type),
        entries: approved.filter((entry) => entry.type === type),
      });
    });
    return groups;
  }, [selectedDayEntries]);

  /* ---------------------------------------------------------------------- */

  const renderDay = (week: WeekRow, dayIdx: number, skeletonBars: SkeletonBar[]) => {
    const day = week.days[dayIdx];
    const key = week.keys[dayIdx];
    const entries = calendarData.dayMap.get(key) ?? [];
    const outside = !inPeriod(day);
    const today = isToday(day);
    const weekend = day.getDay() === 0 || day.getDay() === 6;
    const cellBg = weekend ? sbg.bg.surfaceSubtle : sbg.bg.surface;
    const selected = selectedDayKey === key;
    const hasEntries = entries.length > 0 && !showSkeleton;
    const lanesShown = Math.min(week.laneCount, laneLimit);
    // sm+: entries whose lane is past the limit; phones (week view): the day's own list.
    const hiddenInLanes = entries.filter((entry) => (week.lanes.get(entry._id) ?? 0) >= laneLimit).length;
    const phoneOverflow = entries.length - Math.min(entries.length, laneLimit);
    // Phones draw no lane overlay, so each day shows the placeholder bars that cross it.
    const phoneSkeletons = skeletonBars.filter((bar) => dayIdx >= bar.startCol && dayIdx < bar.startCol + bar.span).length;

    const content = (
      <>
        <DayHeader
          day={day}
          today={today}
          outside={outside}
          weekdayOnPhone={view === 'week'}
          people={showSkeleton ? 0 : peopleIn(entries)}
          countDisplay={view === 'week' ? { xs: 'inline' } : { xs: 'none', lg: 'inline' }}
        />

        {/* Loading on phones: bars when days stack (week), a short pill in the narrow month grid. */}
        {phoneSkeletons > 0 && view === 'week' && (
          <Box aria-hidden="true" sx={{ display: { xs: 'grid', sm: 'none' }, gap: '2px' }}>
            {Array.from({ length: phoneSkeletons }).map((_, n) => (
              <Skeleton
                key={n}
                variant="rounded"
                height={20}
                width={`${60 + ((dayIdx + n) % 3) * 15}%`}
                sx={{ borderRadius: sbg.radius.sm }}
              />
            ))}
          </Box>
        )}
        {phoneSkeletons > 0 && view === 'month' && (
          <Box aria-hidden="true" sx={{ display: { xs: 'flex', sm: 'none' } }}>
            <Skeleton variant="rounded" height={8} width={phoneSkeletons > 1 ? '80%' : '50%'} sx={{ borderRadius: sbg.radius.pill }} />
          </Box>
        )}

        {/* sm+: reserve the week's lanes; the bars themselves are drawn across the row. */}
        {!showSkeleton && lanesShown > 0 && (
          <Box aria-hidden="true" sx={{ display: { xs: 'none', sm: 'block' }, flex: 'none', height: lanesShown * LANE_PITCH - 2 }} />
        )}
        {hasEntries && hiddenInLanes > 0 && <MoreLine count={hiddenInLanes} smUp />}

        {/* Phones, week view: days stack, so each day lists its own entries. */}
        {hasEntries && view === 'week' && (
          <Box sx={{ display: { xs: 'grid', sm: 'none' }, gap: '2px', minWidth: 0 }}>
            {entries.slice(0, laneLimit).map((entry) => (
              <EventBar key={entry._id} entry={entry} showType />
            ))}
            {phoneOverflow > 0 && <MoreLine count={phoneOverflow} />}
          </Box>
        )}

        {/* Phones, month view: the grid is too narrow for names, so each day shows dots. */}
        {hasEntries && view === 'month' && (
          <Box aria-hidden="true" sx={{ display: { xs: 'flex', sm: 'none' }, flexWrap: 'wrap', alignItems: 'center', gap: '3px' }}>
            {entries.slice(0, 4).map((entry) => (
              <SeriesDot key={entry._id} color={timeOffTypeChipStyle(entry.type).dot} hollow={isPending(entry)} />
            ))}
            {entries.length > 4 && (
              <Typography variant="caption" component="span" sx={{ color: sbg.text.secondary, lineHeight: 1 }}>
                +{entries.length - 4}
              </Typography>
            )}
          </Box>
        )}
      </>
    );

    const cellSx = {
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'stretch',
      justifyContent: 'flex-start',
      textAlign: 'left' as const,
      width: '100%',
      height: '100%',
      minWidth: 0,
      p: { xs: 0.5, sm: 0.75 },
      bgcolor: selected ? sbg.bg.selected : cellBg,
    };

    return (
      <Box
        key={key}
        role="cell"
        aria-current={today ? 'date' : undefined}
        sx={{
          bgcolor: cellBg,
          minWidth: 0,
          minHeight: view === 'month' ? { xs: 56, sm: 104, lg: 120 } : { xs: 56, sm: 320 },
        }}
      >
        {hasEntries ? (
          <ButtonBase
            onClick={(event) => openDay(event.currentTarget, key)}
            aria-haspopup="dialog"
            aria-expanded={selected}
            aria-label={`${formatDate(key, { weekday: true })}${today ? ' (today)' : ''}: ${describeDay(entries)}`}
            sx={{
              ...cellSx,
              transition: `background-color ${sbg.duration.fast} ${sbg.ease.standard}`,
              '&:hover': { bgcolor: selected ? sbg.bg.selected : mix(sbg.action.primary, 5, cellBg) },
              '&.Mui-focusVisible': insetFocus,
            }}
          >
            {content}
          </ButtonBase>
        ) : (
          <Box sx={cellSx}>{content}</Box>
        )}
      </Box>
    );
  };

  const laneLayerSx = {
    display: { xs: 'none', sm: 'grid' },
    position: 'absolute',
    top: LANE_TOP,
    left: 0,
    right: 0,
    gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
    gridAutoRows: '20px',
    columnGap: '1px',
    rowGap: '2px',
    pointerEvents: 'none',
  } as const;

  /** sm+: one bar per entry run, spanning its days, in its week lane. Clicks fall through to the day cells. */
  const renderLanes = (week: WeekRow, skeletonBars: SkeletonBar[]) => {
    if (showSkeleton) {
      if (skeletonBars.length === 0) return null;
      return (
        <Box aria-hidden="true" sx={laneLayerSx}>
          {skeletonBars.map((bar) => (
            <Skeleton
              key={`${bar.startCol}-${bar.lane}`}
              variant="rounded"
              height={20}
              sx={{
                gridColumn: `${bar.startCol + 1} / span ${bar.span}`,
                gridRow: bar.lane + 1,
                ml: BAR_INSET,
                mr: BAR_INSET,
                borderRadius: sbg.radius.sm,
              }}
            />
          ))}
        </Box>
      );
    }
    const visible = week.segments.filter((segment) => segment.lane < laneLimit);
    if (visible.length === 0) return null;
    return (
      <Box aria-hidden="true" sx={laneLayerSx}>
        {visible.map((segment) => (
          <EventBar
            key={`${segment.entry._id}-${segment.startCol}`}
            entry={segment.entry}
            outside={segment.outside}
            showType={view === 'week'}
            joinStart={segment.joinStart}
            joinEnd={segment.joinEnd}
            placement={{
              gridColumn: `${segment.startCol + 1} / span ${segment.span}`,
              gridRow: segment.lane + 1,
              ml: segment.joinStart ? 0 : BAR_INSET,
              mr: segment.joinEnd ? 0 : BAR_INSET,
            }}
          />
        ))}
      </Box>
    );
  };

  const legendSwatch = { width: 14, height: 10, borderRadius: '3px', flex: 'none' } as const;

  return (
    <Box
      component="section"
      aria-label="Time-off calendar"
      sx={{
        bgcolor: sbg.bg.surface,
        border: `1px solid ${sbg.border.default}`,
        borderRadius: sbg.radius.card,
        overflow: 'hidden',
        minWidth: 0,
      }}
    >
      {/* Toolbar: period, scope line, view and navigation */}
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          px: { xs: 2, sm: 2.5 },
          py: 2,
          borderBottom: `1px solid ${sbg.border.subtle}`,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h3" component="h2" aria-live="polite" sx={{ color: sbg.text.primary, fontVariantNumeric: 'tabular-nums' }}>
            {periodLabel}
          </Typography>
          <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, mt: 0.25, fontVariantNumeric: 'tabular-nums' }}>
            {summary}
          </Typography>
          {refreshFailed && (
            <Box role="status" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 0.75, mt: 0.5, color: sbg.status.warning.text }}>
              <TriangleAlert size={14} aria-hidden="true" />
              <Typography variant="body2" component="span" sx={{ color: 'inherit', fontVariantNumeric: 'tabular-nums' }}>
                Couldn’t refresh.{' '}
                {fulfilledTimeStamp ? `Showing data from ${formatTime(new Date(fulfilledTimeStamp))}.` : 'Showing the last data loaded.'}
              </Typography>
              <Button variant="text" size="small" onClick={() => refetch()} sx={{ height: 24, minHeight: 24, px: 0.75 }}>
                Try again
              </Button>
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <SegmentedControl
            size="small"
            aria-label="Calendar view"
            value={view}
            onChange={handleViewChange}
            options={[
              { value: 'month', label: 'Month' },
              { value: 'week', label: 'Week' },
            ]}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title={view === 'month' ? 'Previous month' : 'Previous week'}>
              <IconButton size="small" aria-label={view === 'month' ? 'Previous month' : 'Previous week'} onClick={handlePrev}>
                <ChevronLeft />
              </IconButton>
            </Tooltip>
            <Button size="small" variant="outlined" onClick={handleToday}>
              Today
            </Button>
            <Tooltip title={view === 'month' ? 'Next month' : 'Next week'}>
              <IconButton size="small" aria-label={view === 'month' ? 'Next month' : 'Next week'} onClick={handleNext}>
                <ChevronRight />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        {/* A 2px bar on the toolbar's hairline while time off loads or refreshes. */}
        {showProgress && (
          <LinearProgress
            aria-label={loading ? 'Loading time off' : 'Updating time off'}
            sx={{ position: 'absolute', left: 0, right: 0, bottom: -1, height: 2, borderRadius: 0, zIndex: 1 }}
          />
        )}
      </Box>

      {loadFailed ? (
        <Box sx={{ display: 'grid', justifyItems: 'center', pb: failure?.detail ? 4 : 0, color: sbg.text.secondary }}>
          <ErrorState
            title="Couldn’t load time off"
            body={failureAdvice(failure, 'the time-off calendar')}
            onRetry={() => {
              refetch();
            }}
          />
          <ErrorDetails detail={failure?.detail} />
        </Box>
      ) : (
        <Box role="table" aria-label={`Time off, ${periodLabel}`} aria-busy={busy || undefined}>
          {/* Weekday header (hidden on phones in week view, where days stack) */}
          <Box role="rowgroup" sx={view === 'week' ? { display: { xs: 'none', sm: 'block' } } : undefined}>
            <Box
              role="row"
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                bgcolor: sbg.bg.surfaceSubtle,
                borderBottom: `1px solid ${sbg.border.subtle}`,
              }}
            >
              {WEEKDAYS.map((weekday) => (
                <Box
                  key={weekday.long}
                  role="columnheader"
                  aria-label={weekday.long}
                  sx={{ px: { xs: 0.5, sm: 1.25 }, py: 1, color: sbg.text.secondary, font: 'var(--sbg-type-label-xs)' }}
                >
                  <Box component="span" aria-hidden="true" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    {weekday.short}
                  </Box>
                  <Box component="span" aria-hidden="true" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                    {weekday.narrow}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Weeks: a 1px gap over the hairline color draws the grid */}
          <Box role="rowgroup" sx={{ display: 'grid', gap: '1px', bgcolor: sbg.border.subtle }}>
            {calendarData.weeks.map((week, weekIdx) => {
              const skeletonBars = showSkeleton ? skeletonBarsFor(week, weekIdx) : [];
              return (
                <Box
                  key={week.keys[0]}
                  role="row"
                  sx={{
                    position: 'relative',
                    display: 'grid',
                    gap: '1px',
                    gridTemplateColumns:
                      view === 'week' ? { xs: '1fr', sm: 'repeat(7, minmax(0, 1fr))' } : 'repeat(7, minmax(0, 1fr))',
                  }}
                >
                  {week.days.map((_, dayIdx) => renderDay(week, dayIdx, skeletonBars))}
                  {renderLanes(week, skeletonBars)}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Legend with the period's request counts */}
      {!loadFailed && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1.5,
            px: { xs: 2, sm: 2.5 },
            py: 1.5,
            borderTop: `1px solid ${sbg.border.subtle}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 2, rowGap: 1 }}>
            <Typography variant="labelXs" component="span" id={`${groupIdPrefix}-legend`} sx={{ color: sbg.text.secondary }}>
              Requests this {periodNoun}
            </Typography>
            <Box
              component="ul"
              aria-labelledby={`${groupIdPrefix}-legend`}
              sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexWrap: 'wrap', columnGap: 2.5, rowGap: 1 }}
            >
              {TIME_OFF_TYPE_ORDER.map((type) => {
                const tone = timeOffTypeChipStyle(type);
                return (
                  <Box component="li" key={type} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                    <Box
                      aria-hidden="true"
                      sx={{ ...legendSwatch, bgcolor: tone.bg, border: `1px solid ${tone.border}`, display: 'grid', placeItems: 'center' }}
                    >
                      <SeriesDot color={tone.dot} size={4} />
                    </Box>
                    <Typography variant="body2" component="span" sx={{ color: sbg.text.secondary }}>
                      {timeOffTypeLabel(type)}
                    </Typography>
                    <Typography variant="labelSm" component="span" sx={{ color: sbg.text.primary, fontVariantNumeric: 'tabular-nums' }}>
                      {loading ? MISSING : formatNumber(periodStats.byType.get(type) ?? 0)}
                    </Typography>
                  </Box>
                );
              })}
              <Box component="li" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                <Box
                  aria-hidden="true"
                  sx={{ ...legendSwatch, border: `1px dashed ${sbg.text.secondary}`, background: pendingHatch(sbg.text.secondary) }}
                />
                <Typography variant="body2" component="span" sx={{ color: sbg.text.secondary }}>
                  Pending approval
                </Typography>
                <Typography variant="labelSm" component="span" sx={{ color: sbg.text.primary, fontVariantNumeric: 'tabular-nums' }}>
                  {loading ? MISSING : formatNumber(periodStats.pending)}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Typography variant="caption" component="p" sx={{ color: sbg.text.secondary }}>
            Select a day to see who’s away.
          </Typography>
        </Box>
      )}

      {/* Day detail */}
      <Popover
        open={Boolean(popoverAnchor && selectedDayKey)}
        anchorEl={popoverAnchor}
        onClose={closeDay}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            role: 'dialog',
            'aria-labelledby': popoverTitleId,
            sx: { width: 400, maxWidth: 'calc(100vw - 32px)', maxHeight: 'min(560px, 72vh)', display: 'flex', flexDirection: 'column', p: 0 },
          },
        }}
      >
        {selectedDayKey && (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 1,
                pl: 2,
                pr: 1,
                pt: 1.75,
                pb: 1.5,
                borderBottom: `1px solid ${sbg.border.subtle}`,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography id={popoverTitleId} variant="h4" component="h3" sx={{ color: sbg.text.primary }}>
                  {formatDate(selectedDayKey, { weekday: true })}
                </Typography>
                <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, mt: 0.25, fontVariantNumeric: 'tabular-nums' }}>
                  {selectedDayEntries.length === 0 ? 'No one is away this day' : describeDay(selectedDayEntries)}
                </Typography>
                {selectedDayEntries.length > 0 && (
                  <Box
                    component="p"
                    sx={{ m: 0, mt: 0.75, display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 1.25, rowGap: 0.5 }}
                  >
                    {countByType(selectedDayEntries).map(({ type, count }) => (
                      <Box
                        key={type}
                        component="span"
                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, color: sbg.text.primary, font: 'var(--sbg-type-label-xs)', fontVariantNumeric: 'tabular-nums' }}
                      >
                        <SeriesDot color={timeOffTypeChipStyle(type).dot} />
                        {formatNumber(count)} {timeOffTypeLabel(type)}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
              <Tooltip title="Close">
                <IconButton size="small" aria-label="Close" onClick={closeDay}>
                  <X />
                </IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ overflowY: 'auto', p: 0.5 }}>
              {dayGroups.map((group) => {
                const headingId = `${groupIdPrefix}-${group.key}`;
                return (
                  <Box component="section" key={group.key} aria-labelledby={headingId} sx={{ '& + &': { mt: 0.5 } }}>
                    <Box
                      component="h4"
                      id={headingId}
                      sx={{ m: 0, display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, pt: 1, pb: 0.5, font: 'var(--sbg-type-label-xs)', color: sbg.text.secondary }}
                    >
                      {group.pending ? (
                        <StatusPill tone="warning">Pending approval</StatusPill>
                      ) : (
                        <>
                          {group.tone && <SeriesDot color={group.tone.dot} />}
                          <Box component="span" sx={{ color: sbg.text.primary }}>
                            {group.label}
                          </Box>
                        </>
                      )}
                      <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatNumber(group.entries.length)}
                      </Box>
                    </Box>
                    <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                      {group.entries.map((entry) => {
                        const multiDay = entry.startKey !== entry.endKey;
                        const details = [
                          entry.department || null,
                          multiDay ? `${formatHours(entry.hours)} total` : formatHours(entry.hours),
                          multiDay ? formatDateRange(entry.startDate, entry.endDate) : null,
                        ]
                          .filter(Boolean)
                          .join(' · ');
                        return (
                          <Box component="li" key={entry._id}>
                            <ButtonBase
                              component={Link}
                              to={`/employees/${entry.employeeId}`}
                              onClick={closeDay}
                              sx={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                px: 1.5,
                                py: 1,
                                borderRadius: sbg.radius.md,
                                textAlign: 'left',
                                color: sbg.text.primary,
                                textDecoration: 'none',
                                transition: `background-color ${sbg.duration.fast} ${sbg.ease.standard}`,
                                '&:hover': { bgcolor: sbg.bg.hover },
                                '&.Mui-focusVisible': insetFocus,
                              }}
                            >
                              <PersonAvatar name={entry.employeeName} size={32} />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="labelMd" component="span" noWrap sx={{ display: 'block', color: sbg.text.primary }}>
                                  {entry.employeeName}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  component="span"
                                  noWrap
                                  sx={{ display: 'block', color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums' }}
                                >
                                  {details}
                                </Typography>
                              </Box>
                              {group.pending && <TimeOffTypeChip type={entry.type} pending />}
                              <Box component="span" aria-hidden="true" sx={{ display: 'grid', color: sbg.text.secondary }}>
                                <ChevronRight size={16} />
                              </Box>
                            </ButtonBase>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                );
              })}
            </Box>
            <Box sx={{ px: 2, py: 1.25, borderTop: `1px solid ${sbg.border.subtle}` }}>
              <Typography variant="caption" component="p" sx={{ color: sbg.text.secondary }}>
                Select a person to open their employee record.
              </Typography>
            </Box>
          </>
        )}
      </Popover>
    </Box>
  );
};

export default TimeOffCalendarView;
